import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import parseDuration from 'parse-duration';

import { Fields, defaultValues, prepareInput } from 'app/src/elements/forms';
import { Button } from 'app/src/elements/buttons';
import { Loading } from 'app/src/elements/DataState';
import { Title, Text } from 'app/src/styles';
import { datetimeShort } from 'app/src/utils/formatting';
import {
  useNavigate,
  useOutletContext,
  useParams,
} from 'app/src/utils/routing';

import EventThings from './EventThings';
import { EVENT, EVENT_SELECTS, UPDATE_EVENT } from './queries';
import { FIELDS } from './new';

const Event = () => {
  const { id } = useParams();

  const [eventThings, setEventThings] = useState({});
  const [formStatus, setFormStatus] = useState();

  const navigate = useNavigate();
  const client = useOutletContext()?.client;

  const {
    control, handleSubmit, reset, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({ fields: FIELDS })
  });

  const { data, refetch } = useQuery(EVENT, {
    variables: { id },
    fetchPolicy: 'network-only'
  });

  const { data: selects } = useQuery(EVENT_SELECTS);

  useEffect(() => {
    if (data) {
      const { durationDescription, ...event } = data.event;

      let resourceEventAt = null;
      let resourceEventOn = null;

      resourceEventOn = data.event.eventAt.split('T')[0];
      resourceEventAt = DateTime.fromISO(data.event.eventAt)
        .toFormat("yyyy-MM-dd'T'HH:mm");

      const resource = {
        ...event,
        eventAt: resourceEventAt,
        eventOn: resourceEventOn,
      };

      if (durationDescription) {
        resource.duration = durationDescription;
      }

      reset(defaultValues({ fields: FIELDS, resource }));

      const eventThingsData = {};
      data.event.eventThings.forEach(eventThing => {
        eventThingsData[eventThing.thing.id] = {
          thingId: eventThing.thing.id,
          selected: true,
          notes: eventThing.notes,
        };
      });

      setEventThings(eventThingsData);
    }
  }, [data]);

  const [updateEvent] = useMutation(UPDATE_EVENT, {
    onCompleted: () => {
      const eventsPath = client ? `/clients/${client.id}/events` : '/events';
      navigate(eventsPath);
    }
  });

  const onSubmit = ({ eventOn, ...otherInput }) => {
    setFormStatus('submitting');

    const input = prepareInput(otherInput, FIELDS);

    if ('date_only' === input.timeType) {
      input.eventAt = DateTime.fromISO(`${eventOn}T08:00`).toISO();
    }

    if (otherInput.duration) {
      otherInput.duration = parseDuration(otherInput.duration, 's');
    } else {
      otherInput.duration = null;
    }

    input.eventThingsAttributes = Object.keys(eventThings)
      .map(thingId => eventThings[thingId])
      .filter(eventThing => eventThing.selected)
      .map(eventThing => (
        { thingId: eventThing.thingId, notes: eventThing.notes }
      ));

    updateEvent({
      variables: { input: { id, ...input } }
    });
  };

  const timeType = watch('timeType');
  const eventAt = watch('eventAt');

  const fields = { ...FIELDS };

  if ('date_only' === timeType) {
    if ('datetime' === fields.eventAt.type) {
      setValue('eventOn', eventAt.split('T')[0]);
      fields.eventAt = { ...fields.eventAt, type: 'hidden', rules: { } };
      fields.eventOn = {
        ...fields.eventOn,
        type: 'date',
        rules: { required: true }
      };
    }
  } else {
    fields.eventAt = {
      ...fields.evenAt,
      type: 'datetime',
      rules: { required: true }
    };
    fields.eventOn = { ...fields.eventOn, type: 'hidden', rules: { } };
  }

  if (selects) {
    fields.clientId = { ...fields.clientId };

    if (!client) {
      fields.clientId.type = null;
      fields.clientId.select = [
        { value: '', label: 'Select' },
        ...selects.clients
          .map(entry => ({ value: entry.id, label: entry.name }))
      ];
    }

    fields.eventTypeId = {
      ...fields.eventTypeId,
      type: 'button',
      options: selects.eventTypes,
    };
  }

  if (!timeType || !selects) { return <Loading />; }

  const eventType = selects?.eventTypes
    .find(type => type.id === watch('eventTypeId'));

  if (eventType) {
    if (eventType.hasAmount) {
      fields.amount = { ...fields.amount, type: 'Int' };
    }

    if (eventType.amountLabel) {
      fields.amount = { ...fields.amount, label: eventType.amountLabel };
    }

    if (eventType.hasDuration) {
      fields.duration = { ...fields.duration, type: 'string' };
    }
  }

  return (
    <View>
      <Title>
        {data?.event.eventType.name}
        {client && ` (${client.name})`}
      </Title>

      <Fields
        fields={fields}
        control={control}
        errors={errors}
      />

      <Text>
        Entered {datetimeShort(data?.event.createdAt)}.
        {data?.event.updatedAt !== data?.event.createdAt && (
          ` Updated ${datetimeShort(data.event.updatedAt)}.`
        )}
      </Text>

      {eventType?.hasThings && (
        <EventThings
          eventThings={eventThings}
          setEventThings={setEventThings}
          watch={watch}
        />
      )}

      {'submitting' === formStatus ? (
        <Loading />
      ) : (
        <Button label="Save" onPress={handleSubmit(onSubmit)} wide />
      )}
    </View>
  );
};

export default Event;
